import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import {
  useInboundReferralsInsightsActions,
  useInboundReferralsInsightsStore,
} from 'stores/inboundReferralsInsightsStore';

import SlideoutShell, { BorderedSection } from '../../shared/slideout/SlideoutShell';
import { INBOUND_METRIC_DRILL_TITLES } from '../constants';

import AcceptedDetailHeader from './AcceptedDetailHeader';
import InboundEntityDetailHeader from './InboundEntityDetailHeader';
import InboundPatientListSection from './InboundPatientListSection';
import InboundSlideoutTable from './InboundSlideoutTable';
import useInboundSlideoutAnalytics from './useInboundSlideoutAnalytics';

// Orchestrates the inbound drill-down slideout. Switches on the open context to pick the title and
// (for entity drills) the summary header; the breakdown table and patient list are shared.
export default function InboundReferralInsightsSlideout() {
  const slideoutContext = useInboundReferralsInsightsStore((state) => state.slideoutContext);
  const startDate = useInboundReferralsInsightsStore((state) => state.filters[START_DATE]) as Date | undefined;
  const endDate = useInboundReferralsInsightsStore((state) => state.filters[END_DATE]) as Date | undefined;
  const { closeSlideout } = useInboundReferralsInsightsActions();

  const { data, rollups, loading, extraFilters, snfCount } = useInboundSlideoutAnalytics();

  const title =
    slideoutContext?.kind === 'entity'
      ? `${slideoutContext.entityName} \u2013 Overview`
      : slideoutContext?.kind === 'metric'
        ? INBOUND_METRIC_DRILL_TITLES[slideoutContext.metric]
        : '';

  // Only metric drills show the "Referrals to N SNFs" subtitle. An entity drill on a Receiving SNF
  // row has no meaningful SNF count (it's scoped to one SNF), so entity drills omit it entirely.
  const subtitle =
    slideoutContext?.kind === 'metric' ? `Referrals to ${snfCount} SNF${snfCount === 1 ? '' : 's'}` : undefined;

  return (
    <SlideoutShell
      open={slideoutContext !== null}
      onClose={closeSlideout}
      title={title}
      subtitle={subtitle}
      subtitleReady={!loading}
      startDate={startDate}
      endDate={endDate}>
      {slideoutContext && (
        <>
          {slideoutContext.kind === 'entity' && <InboundEntityDetailHeader loading={loading} rollups={rollups} />}
          {slideoutContext.kind === 'metric' && slideoutContext.metric === 'accepted' && (
            <AcceptedDetailHeader loading={loading} rollups={rollups} data={data} />
          )}
          <BorderedSection>
            <InboundSlideoutTable loading={loading} data={data} extraFilters={extraFilters} />
          </BorderedSection>
          <InboundPatientListSection />
        </>
      )}
    </SlideoutShell>
  );
}
